import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import api from '@/api'

export interface Organization {
  id: string
  slug: string
  name: string
  description: string
  logo?: string
  logo_dark?: string
  favicon?: string
  banner?: string
  primaryColor: string
  secondaryColor?: string
  ownerId: string
  ownerEmail: string
  settings: {
    allowPublicCourses: boolean
    requireApproval: boolean
    customDomain?: string
  }
  features: string[]
  stats: {
    totalStudents: number
    totalCourses: number
    totalInstructors: number
    activeEnrollments: number
  }
  createdAt: string
}

export const useOrganizationStore = defineStore('organization', () => {
  const organization = ref<Organization>({
    id: 'eucm-001',
    slug: 'eucm',
    name: 'EUCM - European University of Chinese Medicine',
    description: 'Leading institution for Traditional Chinese Medicine education in Europe',
    logo: '/media/2024/01/logo6.png',
    logo_dark: '',
    favicon: '',
    banner: '/eucm-banner.jpg',
    primaryColor: '#1e40af',
    secondaryColor: '#1e40af',
    ownerId: 'eucm-admin',
    ownerEmail: 'eucm@kuos.ai',
    settings: {
      allowPublicCourses: true,
      requireApproval: false,
      customDomain: 'eucm.dk'
    },
    features: [
      'lms',
      'events',
      'blog',
      'site-builder',
      'cloud-assets',
      'video',
      'audio',
      'meetings',
      'chat',
      'ku-market'
    ],
    stats: {
      totalStudents: 1250,
      totalCourses: 48,
      totalInstructors: 32,
      activeEnrollments: 892
    },
    createdAt: '2024-01-15'
  })

  const isOwner = computed(() => {
    // Only eucm@kuos.ai is the owner/admin of EUCM
    const authStore = useAuthStore()
    return authStore.user?.email === 'eucm@kuos.ai'
  })

  const enabledFeatures = computed(() => organization.value.features)

  const hasFeature = (feature: string) => {
    return organization.value.features.includes(feature)
  }

  const apps = computed(() => [
    {
      id: 'lms',
      name: 'LMS Content',
      description: 'Manage courses, lessons, and students',
      icon: '📚',
      url: '/dashboard',
      color: 'bg-blue-500',
      enabled: hasFeature('lms')
    },
    {
      id: 'events',
      name: 'Events',
      description: 'Create and manage events, webinars, workshops',
      icon: '📅',
      url: '/admin/events',
      color: 'bg-green-500',
      enabled: hasFeature('events')
    },
    {
      id: 'blog',
      name: 'Blog & News',
      description: 'Publish articles, news, and announcements',
      icon: '📝',
      url: '/admin/blog',
      color: 'bg-purple-500',
      enabled: hasFeature('blog')
    },
    {
      id: 'site-builder',
      name: 'Site Builder',
      description: 'Customize your school website',
      icon: '🎨',
      url: '/admin/site',
      color: 'bg-pink-500',
      enabled: hasFeature('site-builder')
    },
    {
      id: 'cloud',
      name: 'Media Library',
      description: 'Access cloud storage and media assets',
      icon: '☁️',
      url: 'https://drive.kuos.ai',
      color: 'bg-cyan-500',
      enabled: hasFeature('cloud-assets'),
      external: true
    },
    {
      id: 'video',
      name: 'Video Studio',
      description: 'Manage video content and recordings',
      icon: '🎬',
      url: 'https://kutube.kuos.ai?org=eucm',
      color: 'bg-red-500',
      enabled: hasFeature('video'),
      external: true
    },
    {
      id: 'audio',
      name: 'Audio & Podcast',
      description: 'Audio courses and podcast management',
      icon: '🎙️',
      url: 'https://podcat.kuos.ai?org=eucm',
      color: 'bg-orange-500',
      enabled: hasFeature('audio'),
      external: true
    },
    {
      id: 'meet',
      name: 'Live Classes',
      description: 'Virtual classrooms and meetings',
      icon: '👥',
      url: 'https://meet.kuos.ai?org=eucm',
      color: 'bg-teal-500',
      enabled: hasFeature('meetings'),
      external: true
    },
    {
      id: 'chat',
      name: 'Community Chat',
      description: 'Student and instructor communication',
      icon: '💬',
      url: 'https://chat.kuos.ai?org=eucm',
      color: 'bg-indigo-500',
      enabled: hasFeature('chat'),
      external: true
    },
    {
      id: 'market',
      name: 'KU Market',
      description: 'Buy and sell educational resources',
      icon: '🛒',
      url: 'https://market.kuos.ai?org=eucm',
      color: 'bg-yellow-500',
      enabled: hasFeature('ku-market'),
      external: true
    },
    {
      id: 'notebook',
      name: 'Research Notes',
      description: 'AI-powered study notes and research',
      icon: '📓',
      url: 'https://notebook.kuos.ai?org=eucm',
      color: 'bg-amber-500',
      enabled: hasFeature('notebook'),
      external: true
    },
    {
      id: 'calendar',
      name: 'Calendar',
      description: 'Schedule classes and events',
      icon: '🗓️',
      url: 'https://calendar.kuos.ai?org=eucm',
      color: 'bg-blue-600',
      enabled: hasFeature('calendar'),
      external: true
    }
  ])

  const availableApps = computed(() => apps.value.filter(app => app.enabled))

  // Fetch organization settings from backend
  async function fetchOrganization() {
    try {
      const response = await api.get('/organization/settings')
      const data = response.data?.settings || response.data || {}
      
      if (Object.keys(data).length > 0) {
        // Update organization with settings from backend
        organization.value = {
          ...organization.value,
          name: data.name || organization.value.name,
          logo: data.logo_url || organization.value.logo,
          logo_dark: data.logo_dark_url || organization.value.logo_dark,
          favicon: data.favicon_url || organization.value.favicon,
          primaryColor: data.primary_color || organization.value.primaryColor,
          secondaryColor: data.secondary_color || organization.value.secondaryColor,
          settings: {
            ...organization.value.settings,
            customDomain: data.custom_domain || organization.value.settings.customDomain
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch organization settings:', error)
    }
  }

  // Update organization from settings page
  function updateFromSettings(settings: any) {
    organization.value = {
      ...organization.value,
      name: settings.name || organization.value.name,
      logo: settings.logo_url || organization.value.logo,
      logo_dark: settings.logo_dark_url || organization.value.logo_dark,
      favicon: settings.favicon_url || organization.value.favicon,
      primaryColor: settings.primary_color || organization.value.primaryColor,
      secondaryColor: settings.secondary_color || organization.value.secondaryColor
    }
  }

  return {
    organization,
    isOwner,
    enabledFeatures,
    hasFeature,
    apps,
    availableApps,
    fetchOrganization,
    updateFromSettings
  }
})
